/* eslint-disable no-magic-numbers */
import { CA_DATE_FORMAT, CA_DATE_FORMAT_DEFAULT } from '@ds/constants'
import { useGlobalStore } from '@vuejs/stores/GlobalStore'
import {
	add,
	format,
	isValid,
	parse,
} from 'date-fns'

import { TIME } from '../helpers/_constants'

export function parseDate(date: string): Date | null {
	const trimmedDate = date.trim()

	const hasMinimumDateParts =
		trimmedDate.includes('/') && trimmedDate.split('/').length >= 3 ||
		trimmedDate.includes('-') && trimmedDate.split('-').length >= 3 ||
		trimmedDate.length >= 8 && /^\d{4}-\d{2}-\d{2}$/.test(trimmedDate) ||
		trimmedDate.length === 8 && /^\d{8}$/.test(trimmedDate)

	if (!hasMinimumDateParts) {
		return null
	}

	if (trimmedDate.length === 8 && /^\d{8}$/.test(trimmedDate)) {
		const month = trimmedDate.substring(0, 2)
		const day = trimmedDate.substring(2, 4)
		const year = trimmedDate.substring(4, 8)
		const formattedDateString = `${month}/${day}/${year}`

		const parsed = parse(formattedDateString, 'MM/dd/yyyy', new Date())

		if (isValid(parsed)) {
			return parsed
		}
	}

	for (const formatString of CA_DATE_FORMAT) {
		const parsed = parse(trimmedDate, formatString, new Date())

		if (isValid(parsed)) {
			return parsed
		}
	}

	return null
}

export const formatRelativeLocale = {
	lastWeek: '\'last\' eeee',
	nextWeek: '\'next\' eeee',
	other: 'MMMM do, yyyy',
	today: '\'today\'',
	tomorrow: '\'tomorrow\'',
	yesterday: '\'yesterday\'',
}

/**
 * Generate a random string to append where needed
 *
 * @example appendRandomString() // "1234567890"
 * The current time is impossible to duplicate, but when multiple fields load
 * at the same time, there can be duplicates, so we will add a Math.random as well
 * @returns {string}
 */

// This function was mainly created to help prevent autocompletion from Chrome. https://gist.github.com/niksumeiko/360164708c3b326bd1c8#gistcomment-3719706
export const appendRandomString = (): string => {
	const hash = Math.random().toString(20).substr(2, 8)

	return hash + Date.now()
}

/**
 * Capitalize first letter in a string:
 *
 * @example capitalize('please try again!') // Please try again!
 * @param {string} value
 * @returns {string}
 */
export const capitalize = (value = ''): string => {
	return value.length > 0 ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value
}

/**
 * Converts date based on locale, defaults to 'en-US':
 *
 * @example convertDateStringToUS('1999-1-1') // 1/1/1999
 * @param {string} date
 * @returns {string}
 */
export const convertDateStringLocale = (date = '', locale = 'en-US'): string => {
	return date ? new Date(date).toLocaleDateString(locale) : ''
}

/**
 * Converts minutes to hours, or decimal hours to hours and minutes
 * - If input is >= 10, treats it as minutes (e.g., 75 -> "1 hr 15 min")
 * - If input is < 10 and treatAsMinutes is false, treats it as decimal hours (e.g., 7.2 -> "7 hr 12 min")
 * - If treatAsMinutes is true, always treats input as minutes (e.g., 2 -> "2 min")
 *
 * @param {number} minutes - Either minutes or decimal hours
 * @param {boolean} treatAsMinutes - Force treating the value as minutes regardless of size (default: false for backward compatibility)
 * @returns {string}
 */
export const minutesToHumanDuration = (minutes: number | null): string => {
	if (!minutes) {
		return '0 hr'
	}

	const hours = Math.floor(minutes / TIME.MINUTES)
	const remainder = Math.round(minutes % TIME.MINUTES)

	// If no full hours, return just minutes
	if (hours === 0) {
		return `${remainder} min`
	}

	// If no remainder, return just hours
	if (remainder === 0) {
		return `${hours} hr`
	}

	// If both hours and remainder, return both
	return `${hours} hr ${remainder} min`
}

/**
 * Converts snake_case to camelCase
 *
 * @example toCamelCase('duration_in_minutes') // durationInMinutes
 * @param {string} value
 * @returns {string}
 */
export const toCamelCase = (value: string): string => {
	return value.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase())
}

/**
 * Converts an object with snake_case keys to camelCase keys
 *
 * @example toCamelCaseObject({ duration_in_minutes: 120 }) // { durationInMinutes: 120 }
 * @param {Record<string, any>} content
 * @returns {Record<string, any>}
 */
export const toCamelCaseObject = (content: Record<string, any>): Record<string, any> => {
	if (content === null || typeof content !== 'object') {
		return content
	}

	if (Array.isArray(content)) {
		return content.map(toCamelCaseObject)
	}

	const result: Record<string, any> = {}

	if (!!content) {
		for (const [
			key,
			value,
		] of Object.entries(content)) {
			result[toCamelCase(key)] = toCamelCaseObject(value)
		}
	}

	return result
}

/**
 * Converts a date string to a standard date format
 *
 * @param {string} completionDate
 * @returns {string}
 */
export const formattedCompletedDate = (completionDate?: string): string => {
	if (!completionDate) {
		return ''
	}

	try {
		const date = new Date(completionDate)

		if (isNaN(date.getTime())) {
			return 'Invalid date'
		}

		return format(date, CA_DATE_FORMAT_DEFAULT)
	} catch {
		return 'Invalid date'
	}
}

/**
 * Converts a date string to a standard date format in UTC
 *
* @param {string} completionDate
* @returns {string}
*/
export const formattedCompletedDateUTC = (completionDate?: string): string | null => {
	if (!completionDate) return null

	try {
		const date = new Date(completionDate)

		if (isNaN(date.getTime())) return null

		// Ensure output is based on UTC, not local time
		const year = date.getUTCFullYear()
		const month = String(date.getUTCMonth() + 1).padStart(2, '0')
		const day = String(date.getUTCDate()).padStart(2, '0')

		return `${month}/${day}/${year}`
	} catch {
		return null
	}
}

/**
 * Format date by weeks ago:
 *
 * @example formatDateByWeek(-5) // Sets date 5 weeks from current date
 * @param {number} weeks
 * @returns {string}
 */
export const formatDateByWeek = (weeks = 0): string => {
	const date = new Date()
	const adjustedDate = add(date, { weeks })

	return format(adjustedDate, CA_DATE_FORMAT_DEFAULT)
}

/**
 * Formats phoneNumber string into North American phone number format:
 *
 * @example formatPhoneNumber('1234567890') // "(123) 456-7890"
 *          formatPhoneNumber('+11234567890') // "+1 (123) 456-7890"
 * @param {string} phoneNumber - where phoneNumber.length >= 10
 * @returns {string}
 */
export const formatPhoneNumber = (phoneNumber: string): string | null => {
	const cleaned = `${phoneNumber}`.replace(/\D/g, '')
	const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)

	if (match) {
		const intlCode = match[1] ? '+1 ' : ''

		return [
			intlCode,
			'(',
			match[2],
			') ',
			match[3],
			'-',
			match[4],
		].join('')
	}

	return null
}

/**
 * Returns the initials of a name:
 *
 * @example getInitials('John Doe') // JD
 *
 * @param {string} name
 * @returns {string}
 */
export const getInitials = (name: string): string => {
	return name.split(' ')[0].charAt(0).toUpperCase() + name.split(' ')[1]?.charAt(0).toUpperCase()
}

/**
 * Returns the plural or singular form of a term based on a number:
 *
 * @example pluralize(2, 'member') // 2 members
 *
 * @param {number} count
 * @param {string} term
 *
 * @returns {string}
 */
export function pluralize(count: number, term: string): string {
	if (term.toLowerCase().endsWith('s'.toLowerCase()) && count === 1) {
		return `${term}`
	} else if (term.toLowerCase().endsWith('s'.toLowerCase())) {
		return `${term}${count > 1 ? 'es' : ''}`
	}

	switch (count) {
		case 1:
			return `${term}`
		default:
			return `${term}s`
	}
}

/**
 * Returns the string with the replaced text:
 * @example replaceText('remove', '', 'removeMember') // 2 members
 *
 * @param {string} replaceText
 * @param {string} replaceTextWith
 * @param {string} text
 *
 * @returns {string}
 */
export function replaceText(replaceText: string, replaceTextWith: string, text: string): string {
	return text.replace(replaceText, replaceTextWith)
}

/**
 * Safely sets the inner HTML of the first element matching the given selector.
 *
 * @param selector - A CSS selector string to identify the target element.
 * @param html - The HTML string to set as the element's inner HTML.
 *
 * @remarks
 * This function does not sanitize the HTML input. Use with caution to avoid XSS vulnerabilities.
 */
export function setInnerHtml(selector: string, html: string) {
	const element = document.querySelector(selector)

	if (element) {
		element.innerHTML = html
	}
}

/**
 * Sets the previous route URL in session storage and GlobalStore
 */
export function setPreviousRouteUrl() {
	const previousURL = document.referrer

	window.sessionStorage.setItem('previousRoute', previousURL)
	useGlobalStore().previousRouteUrl = previousURL
}

/* eslint-enable no-magic-numbers */
